import React, { useState } from 'react';
import { Button, IconButton, Stack, Dialog, DialogTitle, DialogContent, Typography, Box, ButtonGroup } from '@mui/material';
import { CallMade, Check, Close, ArticleOutlined } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormControlProps } from '../../schemed/FormControlProps';
import { TimeDisplay } from '../../schemed/TimeDisplay';
import { FieldType } from '../../../hooks/useSchema';

interface Props extends Omit<FormControlProps, "onChange"> {
  publish: () => void;
  unpublish: () => void;
  createdField?: string;
  updatedField?: string;
}

export const PublicationStatus = (props: Props) => {
  const { row, field, publish, unpublish, createdField = 'created_datetime', updatedField = "updated_datetime" } = props;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { formatMessage } = useIntl();

  const publishedAt = (row || {})[field];
  const updatedAt = (row || {})[updatedField];
  const isPublished = !!publishedAt;

  return (
    <Box sx={{ width: '100%', maxWidth: "100%" }}>
      <ButtonGroup 
          fullWidth 
          size="small" 
          variant="outlined" 
          color={isPublished ? "success" : "primary"} 
          sx={{ height: 32 }}
      >
          {isPublished ? (
              <Button 
                  disableElevation
                  sx={{ pointerEvents: 'none', px: 2, flexGrow: 1, minWidth: 0 }}
                  startIcon={<Check />}
              >
                  <FormattedMessage id="cms.form.is_published" defaultMessage="Опубликовано" />
              </Button>
          ) : (
              <Button 
                  disableElevation 
                  onClick={() => publish()}
                  sx={{ flexGrow: 1, minWidth: 0 }}
                  startIcon={<CallMade />}
              >
                  <FormattedMessage id="cms.form.publish" defaultMessage="Опубликовать" />
              </Button>
          )}
          
          <Button 
              size="small" 
              onClick={() => setIsDialogOpen(true)}
              sx={{ px: 1, minWidth: 0, width: 40,  }}
              title={formatMessage({ id: "cms.form.publishing_status_and_history", defaultMessage: "Статус публикации и история" })}
          >
              <ArticleOutlined fontSize="small" />
          </Button>
      </ButtonGroup>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <FormattedMessage id="cms.form.publishing_status" defaultMessage="Статус публикации" />
              <IconButton onClick={() => setIsDialogOpen(false)} size="small">
                  <Close />
              </IconButton>
          </DialogTitle>
          <DialogContent dividers>
              <Stack spacing={2}>
                  <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        <FormattedMessage id="common.fields.created_at_long" />
                      </Typography>
                      <Typography variant="body2">
                        <TimeDisplay
                          field={createdField}
                          row={row}
                          schema={{ type: FieldType.datetime }}
                          config={{ utcToLocal: true }}
                          />
                      </Typography>
                  </Box>

                  {!!updatedAt &&
                    <Box>
                        <Typography variant="caption" color="textSecondary" display="block">
                          <FormattedMessage id="common.fields.updated_at_long" />
                        </Typography>
                        <Typography variant="body2">
                          <TimeDisplay
                            field={updatedField}
                            row={row}
                            schema={{ type: FieldType.datetime }}
                            config={{ utcToLocal: true }}
                            />
                        </Typography>
                    </Box>}
                  
                  <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        <FormattedMessage id="cms.form.published_datetime" />
                      </Typography>
                      <Typography variant="body2" color={isPublished ? "success.main" : "textPrimary"}>
                          {isPublished
                            ? (
                              <TimeDisplay
                                field={field}
                                row={row}
                                schema={{ type: FieldType.datetime }}
                                config={{ utcToLocal: true }}
                                />
                            )
                            : "-"}
                      </Typography>
                  </Box>

                  <Box mt={2} display="flex" gap={1}>
                      {isPublished ? (
                          <Button 
                              variant="outlined" 
                              color="error" 
                              size="small"
                              onClick={() => {
                                  unpublish();
                                  setIsDialogOpen(false);
                              }}
                              fullWidth
                          >
                            <FormattedMessage id="cms.form.unpublish" defaultMessage="Снять с публикации" />
                          </Button>
                      ) : (
                          <Button 
                              variant="contained" 
                              color="primary" 
                              size="small"
                              onClick={() => {
                                  publish();
                                  setIsDialogOpen(false);
                              }}
                              fullWidth
                          >
                              <FormattedMessage id="cms.form.publish" defaultMessage="Опубликовать" />
                          </Button>
                      )}
                  </Box>
              </Stack>
          </DialogContent>
      </Dialog>
    </Box>
  );
};